import { User } from './user';
import { CoordinatorService } from './coordinator.service';
import { BehaviorSubject, Subscription } from 'rxjs';
import { Unsubscribe } from 'firebase/firestore';
import { CloudTeamManager } from './cloud-team-manager';
import { CloudChannel } from './cloud-channel';
import { Temporal } from '@js-temporal/polyfill';
// import { CloudAttachmentManager } from './cloud-attachment-manager';
import { user, beforeAuthStateChanged } from '@angular/fire/auth';
import { auth as UI } from 'firebaseui';
import firebaseUIConfig from '../firebase/ui-config';
import { UserGroup } from './user-group';
import * as Sentry from '@sentry/angular';
export class User$ {
    loginUI!: UI.AuthUI;

    private currentUserSubject = new BehaviorSubject<User | undefined>(undefined);

    authService$: any;
    private _userSubscription!: Subscription; //needed for permanent subscription to user auth state

    current$ = this.currentUserSubject.asObservable();

    private _unlogged?: User;

    lastCloudOpenMap = new Map<string, Temporal.Instant>();

    // debugUserLs: User[]
    private _userMap = new Map<string, User>();
    private _userGroupMap = new Map<string, UserGroup>();

    userLatestUpdateMap = new Map<string, number>();

    // cloudAttachmentManager: CloudAttachmentManager | undefined = undefined;
    cloudTeamManager: CloudTeamManager | undefined = undefined;
    // cloudUpdateManager: CloudUpdateManager | undefined = undefined
    cloudChannel: CloudChannel | undefined = undefined;

    private _querySubscriptionMap = new Map<string, Unsubscribe>();

    constructor(public coords: CoordinatorService) {}

    async onStart() {
        // console.log("USER", coords.auth.currentUser)
        this.authService$ = user(this.coords.auth);
        this.loginUI = new UI.AuthUI(this.coords.auth);

        if (this.coords.display$.targetSite('&beta.referentka.app')) {
            console.log('Enabling whitelisting for beta.referentka.app...');
            this._allowOnlyWhitelistedUsers();
        }

        this._userSubscription = this.authService$.subscribe((user: any) => {
            if (!user || !user.emailVerified) {
                return;
            }

            // console.log('setting current user to: ', user);
            this.setCurrentFromFirebase(user);

            if (Sentry.isInitialized()) {
                Sentry.setUser({ id: user.uid, email: user.email });
            }
        });
    }

    async historicInvitedUserLs() {
        const resp = await this.coords.firebase$.callFunction('invitedUserList', {});
        if (resp === 'ERROR') return [];
        return resp.data as unknown as any[];
    }

    async initUserGroups(u: User) {
        const resp = await this.coords.firebase$.firestore.getObjectLs('userGroups', {
            field: 'userId',
            value: u.id,
        });
        resp.forEach((x: any) => {
            const ug = new UserGroup(this.coords, x);
            this._userGroupMap.set(ug.id, ug);
        });
    }

    deleteUserGroup(ug: UserGroup) {
        this._userGroupMap.delete(ug.id);
        this.coords.display$.markForCheck();
    }

    async updateUserGroup(params: any) {
        let ug = this._userGroupMap.get(params.id);
        if (ug) {
            ug.title = params.title;
            ug.updateUserLs(params.guLs);
        } else {
            ug = new UserGroup(this.coords, params);
            this._userGroupMap.set(ug.id, ug);
        }
        await ug.upload();
    }

    get userGroupLs() {
        const ls = Array.from(this._userGroupMap.values());
        ls.sort((a: UserGroup, b: UserGroup) => a.title.localeCompare(b.title));
        return ls;
    }

    startLoginProcess() {
        this.loginUI.start('#firebaseuiAuthContainer', firebaseUIConfig);
    }

    clear() {
        // this.cloudAttachmentManager?.clear();
        this.cloudChannel?.clear();
    }

    async terminate() {
        localStorage.removeItem('extId');
        localStorage.removeItem('extT');
        await this.closeQuerySubscription('all');
        for (let i = 0; i < 10; i++) {
            if (!this.coords.hasInProgressActions) break;
            await this.coords.utils$.wait(i);
        }
        this._userMap.clear();
        this._userGroupMap.clear();
        // this.cloudAttachmentManager = undefined;
        this.cloudTeamManager = undefined;
        this.cloudChannel = undefined;
        this._querySubscriptionMap.clear();
        this.userLatestUpdateMap.clear();
        this.lastCloudOpenMap.clear();
        this.currentUserSubject.next(undefined);
    }

    async closeQuerySubscription(k: string) {
        if (k == 'all') for (const uns of this._querySubscriptionMap.values()) await uns();
        else await this._querySubscriptionMap.get(k)?.();
    }

    async addQuerySubscription(key: string, unsubscribe: Unsubscribe) {
        await this.closeQuerySubscription(key);
        this._querySubscriptionMap.set(key, unsubscribe);
    }

    get isLogged() {
        return !!this.current;
    }

    get hasTeam() {
        return this._userMap.size > 1;
    }

    projectChange(projectId: string) {
        this._userMap.forEach((u) => u.clearCloudData());
        // this.cloudUpdateManager?.projectChange(projectId)
    }

    get currentId() {
        const user = this.current;
        return user ? user.id : '';
    }

    get current() {
        return this.currentUserSubject.value;
    }

    get unloggedUser() {
        if (!this._unlogged)
            this._unlogged = new User(this.coords, {
                id: '_unlogged',
                name: this.coords.text('me'),
                receiveNotifications: false,
                isCurrent: true,
            });
        return this._unlogged;
    }

    get hasActiveOtherUser() {
        return this.activeLs().length !== 0;
    }

    get allLs() {
        return Array.from(this._userMap.values());
    }

    activeLs(includeCurrent = false) {
        return Array.from(this._userMap.values()).filter(
            (u) => u.isActive && (includeCurrent || !u.isCurrent),
        );
    }

    addUser(u: User) {
        this._userMap.set(u.id, u);
    }

    async refreshUserLs(userIdLs: string[]) {
        const uLs: Promise<any>[] = [];
        userIdLs
            .filter((userId) => !this._userMap.has(userId))
            .forEach((userId) => this.updateUser(userId));
        await Promise.all(uLs);
    }

    async updateUser(userId: string) {
        console.log('UPDATE USER', userId);
        try {
            const uData = (await this.coords.firebase$.firestore.getObject('users', userId)) as any;
            if (uData) {
                uData.userId = userId;
                this.addUser(new User(this.coords, uData));
            }
        } catch (e) {
            this.coords.utils$.consoleError('updateUser failed', e);
        }
    }

    updateCurrentUser({ uid, name }: { uid: string; name: string }) {
        if (this.current?.id !== uid) return;

        const updatedUser = new User(this.coords, {
            id: uid,
            name: name,
            email: this.current.email,
            isCurrent: true,
            isAdmin: this.current.isAdmin,
        });

        this._userMap.set(uid, updatedUser);
        this.currentUserSubject.next(updatedUser);
    }

    async logout() {
        await this.terminate();
        await this.coords.auth.signOut();

        if (Sentry.isInitialized()) {
            Sentry.setUser(null);
        }

        location.reload();
    }

    public async setCurrentFromFirebase(
        user: {
            uid: string | null;
            email: string | null;
            displayName: string | null;
        } | null,
    ) {
        if (!user) {
            this.currentUserSubject.next(undefined);
            return;
        }

        const userData = await this.coords.firebase$.firestore.getObject('users', user.uid!);

        let userName: string;
        let receiveNotifications = true;
        let isAdmin = false;

        if (userData) {
            userName = userData['name'];
            receiveNotifications = userData['receiveNotifications'] !== false;
            isAdmin = userData['isAdmin'] === true;
        } else {
            userName = (user.displayName || user.email)!;
        }

        const appUser = new User(this.coords, {
            id: user.uid,
            name: userName,
            email: user.email,
            receiveNotifications: receiveNotifications,
            isCurrent: true,
            isAdmin,
        });

        if (!userData) {
            appUser.updateData({});
            user.email && appUser.setCloudTag(user.email, true);
        }

        this.currentUserSubject.next(appUser);
        this.addUser(appUser);
        const pcode = await this.coords.filesystem$.getStartupFile('_pcode', {
            expireSeconds: 15 * 60,
            deleteAfterRead: true,
        });
        console.log('START PCODE', pcode);
        pcode && this.coords.project$.urlPcode(pcode);
    }

    get(userId: string) {
        return this._userMap.get(userId);
    }

    loginDialog(message?: { prefix?: string; suffix?: string }) {
        this.coords.display$.dialog$.next({
            action: 'login',
            message,
        });
    }

    logoutDialog() {
        this.coords.upload$.beforeUnload();
        this.coords.display$.dialog$.next({
            action: 'logout',
        });
    }

    manifestLoad() {
        this.coords.manifest$.load('team');
    }

    manifestLoadResponse(response: { resultMap: Map<string, FileSystemFileHandle[]> }) {
        response.resultMap.forEach((ls, k) => {
            const u = this._userMap.get(k);
            if (!u) return;
            u.fileHandleLs = ls;
        });
        // this.processActiveUserFiles()
    }

    editDialog() {
        this.coords.display$.dialog$.next({
            action: 'user-detail',
        });
    }

    private _allowOnlyWhitelistedUsers() {
        beforeAuthStateChanged(this.coords.auth, async (user) => {
            if (!user || !user.email) return;

            const isUserWhitelisted = await this.coords.firebase$.firestore.getObject(
                'accessLists/users/whitelist',
                user.email.toLowerCase(),
            );

            if (!isUserWhitelisted) {
                this.loginUI.reset();
                const messageElement = document.getElementById('firebaseui-message');
                if (messageElement) {
                    messageElement.innerHTML = `This account is not allowed to sign in.
                    Please contact <a href="mailto:info@referentka.app" target="_blank">info@referentka.app</a>.`;
                }
                throw new Error('Account is not allowed to sign in');
            }
        });
    }
}
